import {
  isTokenExpired,
  needsTokenRefresh,
} from "@/lib/shared/auth-utils";
import { withTokenRefreshLock } from "@/lib/client/token-refresh-lock";
import { create } from "zustand";
import { AuthStore, AuthUser, UserInfo } from "../types/auth";

// ---------------------------------------------------------------------------
// Token model (#39 — see docs/auth-audit.md §4)
//   - Access token  -> in-memory Zustand state only (never persisted to localStorage).
//   - Refresh token -> HttpOnly cookie set by the server; not JS-readable. The refresh
//     call sends it automatically via `credentials: 'include'`.
//   - user-info     -> localStorage (non-sensitive; used to re-hydrate the UI on
//     reload, and as the marker that a session may be silently re-bootstrappable).
//
// Cross-tab coordination (#37 — preserved). A single shared in-flight promise plus a
// cross-tab Web Lock keep only one tab rotating at a time. Because the refresh token
// now lives in a SHARED cookie (not per-tab localStorage), a tab that rotates updates
// the cookie for every tab automatically; we additionally broadcast the fresh in-memory
// access token over a BroadcastChannel so the other tabs adopt it without their own
// network round-trip, and broadcast logout so every tab tears down together. (The old
// `storage`-event listener no longer fires — we don't write tokens to localStorage.)
// ---------------------------------------------------------------------------

// A1: a single shared in-flight refresh promise. EVERY caller (the proactive monitor,
// the axios 401 interceptor, the wake/visibility handlers and the reload bootstrap)
// awaits this same promise — we never start a second refresh while one is running.
let refreshing: Promise<boolean> | null = null;

let refreshInterval: ReturnType<typeof setInterval> | null = null;

// Idempotency guard for the cross-tab + wake listeners.
let clientListenersReady = false;

const REFRESH_URL = "/api/v1/auth/refresh";
const LOGOUT_URL = "/api/v1/auth/logout";

// CSRF defense for the cookie-borne refresh/logout: a custom header a cross-site HTML
// form cannot set, and that a cross-origin fetch cannot send without a (denied) CORS
// preflight. The server enforces it on the cookie-authenticated refresh path.
const CSRF_HEADERS: Record<string, string> = {
  "X-Requested-With": "XMLHttpRequest",
};

// Transient (5xx / network) retry policy. Transient failures must NOT log the user out
// (A3) — we back off and retry, leaving the session in place.
const MAX_TRANSIENT_RETRIES = 3;
const BASE_BACKOFF_MS = 300;

// BroadcastChannel name for cross-tab auth events (token adoption + logout mirroring).
const AUTH_CHANNEL_NAME = "condota-auth";

const readStorage = (key: string): string | null =>
  typeof window !== "undefined" ? localStorage.getItem(key) : null;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const backoffDelay = (attempt: number): number =>
  Math.min(BASE_BACKOFF_MS * 2 ** attempt, 2000);

const parseUserInfo = (raw: string | null): UserInfo | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return undefined;
  }
};

/**
 * Remove any legacy / transient JS-readable tokens from localStorage:
 *   - a pre-#39 session's access + refresh token (migrated onto the cookie on the
 *     first successful refresh),
 *   - the transient partial access token written during an MFA challenge.
 * `user-info` is intentionally kept (non-sensitive UI/bootstrap marker).
 */
const clearLegacyTokenStorage = (): void => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("auth-token");
  localStorage.removeItem("refresh-token");
};

let authChannel: BroadcastChannel | null = null;
const getAuthChannel = (): BroadcastChannel | null => {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!authChannel) {
    try {
      authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    } catch {
      authChannel = null;
    }
  }
  return authChannel;
};

const broadcastTokens = (token: string): void => {
  try {
    getAuthChannel()?.postMessage({ type: "tokens", token });
  } catch {
    // best-effort cross-tab sync
  }
};

const broadcastLogout = (): void => {
  try {
    getAuthChannel()?.postMessage({ type: "logout" });
  } catch {
    // best-effort cross-tab sync
  }
};

type RefreshOutcome =
  | { ok: true }
  // terminal === true → the refresh cookie/token is genuinely invalid (confirmed 401),
  // so the session must be torn down. terminal === false → a transient failure
  // (5xx / network / 403), so we keep the session and let a later attempt recover.
  | { ok: false; terminal: boolean };

/**
 * Perform the actual network rotation against /refresh.
 *
 * The HttpOnly refresh cookie is sent automatically by `credentials: 'include'`. A
 * legacy pre-#39 session may still have a JS-readable refresh token in localStorage;
 * we send it in the body as a one-time migration fallback (dual-read). On the first
 * success the server (re)sets the cookie and we delete the legacy token for good.
 *
 *   - 200            → success; adopt the new access token, drop the legacy token.
 *   - 5xx / network  → transient: retry with backoff, never terminal (A3 / audit #4).
 *   - 401            → confirmed invalid (the server's grace window replays the
 *                      successor on legitimate concurrent refreshes, so a real 401 is
 *                      terminal — a logout).
 *   - other (e.g.403)→ non-terminal failure (do not force a logout).
 */
async function networkRefresh(
  applyTokens: (tokens: { token: string; refreshToken: string }) => void
): Promise<RefreshOutcome> {
  let transientAttempts = 0;

  while (true) {
    const legacyRefreshToken = readStorage("refresh-token");

    let response: Response | null = null;
    let threw = false;
    try {
      response = await fetch(REFRESH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...CSRF_HEADERS },
        credentials: "include",
        body: JSON.stringify(
          legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {}
        ),
      });
    } catch {
      threw = true;
    }

    // Transient infra failure: retry with backoff, keep the session (A3 / audit #4).
    if (threw || (response !== null && response.status >= 500)) {
      if (transientAttempts < MAX_TRANSIENT_RETRIES) {
        await sleep(backoffDelay(transientAttempts));
        transientAttempts += 1;
        continue;
      }
      return { ok: false, terminal: false };
    }

    if (response!.ok) {
      const data = await response!.json();
      applyTokens({ token: data.token, refreshToken: data.refreshToken });
      // The server has now (re)set the HttpOnly cookie; drop any legacy JS-readable
      // token so the refresh secret can never be exfiltrated from storage again.
      clearLegacyTokenStorage();
      return { ok: true };
    }

    if (response!.status === 401) {
      // Confirmed invalid/expired/revoked refresh credential. Legitimate concurrent
      // refreshes are served the rotated successor by the server's grace window (#42)
      // and return 200, so a 401 here is genuinely terminal.
      return { ok: false, terminal: true };
    }

    // Any other non-OK status (e.g. 403 missing CSRF, 400) is unexpected for the SPA.
    // Do not force a logout — only a confirmed 401 invalid-token does that.
    return { ok: false, terminal: false };
  }
}

export const useAuthStore = create<AuthStore>((set, get) => {
  /**
   * The single coordinated refresh routine. Runs under the cross-tab lock and, once it
   * holds the lock, skips the network call if another tab already rotated (and
   * broadcast the fresh access token) while we were waiting.
   */
  const performRefresh = async (): Promise<boolean> => {
    // Snapshot the in-memory access token BEFORE contending for the lock so we can
    // detect a rotation broadcast that arrived while we waited.
    const tokenBeforeLock = get().user?.token ?? null;

    return withTokenRefreshLock(async () => {
      const tokenAfterLock = get().user?.token ?? null;
      if (
        tokenAfterLock &&
        tokenAfterLock !== tokenBeforeLock &&
        !isTokenExpired(tokenAfterLock)
      ) {
        // Another tab rotated the shared cookie and broadcast its fresh access token
        // while we waited — adopt it, no network call.
        return true;
      }

      const outcome = await networkRefresh((tokens) =>
        get().updateTokens(tokens)
      );

      if (outcome.ok) return true;
      if (outcome.terminal) {
        // Confirmed invalid → consistent teardown via the store.
        await get().logout();
      }
      // Transient failure: leave the session intact and report failure so the caller
      // can surface a transient error (not a logout).
      return false;
    });
  };

  /** Refresh only if the current access token is at/near expiry (A4). */
  const refreshIfNeeded = async (): Promise<void> => {
    const state = get();
    if (!state.isAuthenticated) return;
    const token = state.user?.token ?? null;
    if (token && needsTokenRefresh(token)) {
      await get().refreshToken();
    }
  };

  /**
   * Re-bootstrap a session purely from the HttpOnly refresh cookie (a hard reload
   * wiped the in-memory access token, but the cookie survives). Returns true if the
   * cookie was valid and a fresh session was established.
   */
  const bootstrapFromCookie = async (
    cachedUserInfo: UserInfo | undefined
  ): Promise<boolean> => {
    let response: Response | null = null;
    try {
      response = await fetch(REFRESH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...CSRF_HEADERS },
        credentials: "include",
        body: "{}",
      });
    } catch {
      return false; // network error during bootstrap → treat as unauthenticated
    }

    if (!response.ok) return false;

    const data = await response.json();
    if (!data?.token) return false;

    set({
      user: {
        token: data.token,
        refreshToken: data.refreshToken ?? "",
        userInfo: cachedUserInfo,
      },
      isAuthenticated: true,
    });
    clearLegacyTokenStorage();
    get().startTokenRefreshMonitoring();
    broadcastTokens(data.token);

    // If user-info wasn't cached (e.g. it was cleared), fetch it now so the UI has it.
    if (!cachedUserInfo) {
      try {
        const me = await fetch("/api/v1/auth/user", {
          headers: { Authorization: `Bearer ${data.token}` },
        });
        if (me.ok) {
          const info = (await me.json()) as UserInfo;
          set((state) => ({
            user: state.user ? { ...state.user, userInfo: info } : null,
          }));
          if (typeof window !== "undefined") {
            localStorage.setItem("user-info", JSON.stringify(info));
          }
        }
      } catch {
        // best-effort — the UI can fetch user info on demand
      }
    }

    return true;
  };

  /**
   * Register cross-tab + wake listeners exactly once (A2, A4). Kept for the lifetime of
   * the tab and no-op while unauthenticated, so we don't churn add/remove on every
   * login/logout.
   */
  const setupClientListeners = () => {
    if (typeof window === "undefined" || clientListenersReady) return;
    clientListenersReady = true;

    // A2: cross-tab auth events over BroadcastChannel (replaces the #37 `storage`
    // listener — tokens are no longer written to localStorage so that never fires).
    const channel = getAuthChannel();
    if (channel) {
      channel.addEventListener("message", (event: MessageEvent) => {
        const data = event.data;
        if (!data || typeof data !== "object") return;

        if (data.type === "logout") {
          // Another tab logged out — mirror the teardown locally (no network).
          if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
          }
          set({ user: null, isAuthenticated: false });
          return;
        }

        if (data.type === "tokens" && typeof data.token === "string") {
          // Another tab rotated — adopt its fresh in-memory access token.
          const state = get();
          if (!state.isAuthenticated || !state.user) return;
          if (state.user.token === data.token) return;
          set({ user: { ...state.user, token: data.token } });
        }
      });
    }

    // A4: catch up on wake / focus / reconnect, BEFORE the request burst.
    const onWake = () => {
      void refreshIfNeeded();
    };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onWake();
    });
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
  };

  return {
    user: null,
    isAuthenticated: false,
    isInitialized: false,

    login: (user: AuthUser) => {
      if (typeof window !== "undefined") {
        // Access token lives in memory; the refresh token is in the HttpOnly cookie the
        // server set on this response. Persist only the non-sensitive user-info, and
        // drop any transient/legacy JS-readable tokens (an MFA-challenge partial token,
        // or a pre-#39 session) so nothing renewable lingers in storage.
        clearLegacyTokenStorage();
        if (user.userInfo) {
          localStorage.setItem("user-info", JSON.stringify(user.userInfo));
        }
      }
      set({ user, isAuthenticated: true });
      broadcastTokens(user.token);

      // Start token refresh monitoring + cross-tab/wake listeners.
      get().startTokenRefreshMonitoring();
    },

    logout: async () => {
      // Stop token refresh monitoring
      get().stopTokenRefreshMonitoring();

      // Skip client-side operations during SSR
      if (typeof window === "undefined") {
        set({ user: null, isAuthenticated: false });
        return;
      }

      // Notify the server: clears the HttpOnly refresh cookie and revokes the refresh
      // token. The cookie is path-scoped to /refresh so the browser does NOT attach it
      // here; we send the current refresh token (kept in memory from the last
      // login/rotation response) in the body so the server can actually revoke the
      // session. credentials:'include' is harmless and the CSRF header is set too.
      const token = get().user?.token ?? localStorage.getItem("auth-token");
      const refreshToken = get().user?.refreshToken;
      try {
        await fetch(LOGOUT_URL, {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
            ...CSRF_HEADERS,
          },
          credentials: "include",
          body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        });
      } catch (error) {
        console.warn("Failed to notify server of logout:", error);
      }

      // Clear local state (user-info + any legacy/transient tokens).
      clearLegacyTokenStorage();
      localStorage.removeItem("user-info");

      set({ user: null, isAuthenticated: false });
      broadcastLogout();
    },

    updateUserInfo: (userInfo: UserInfo) => {
      if (typeof window !== "undefined") {
        localStorage.setItem("user-info", JSON.stringify(userInfo));
      }
      set((state) => ({
        user: state.user ? { ...state.user, userInfo } : null,
      }));
    },

    updateTokens: (tokens: { token: string; refreshToken: string }) => {
      // Guard against a ghost re-auth: if the session was already torn down (e.g. an
      // explicit logout raced an in-flight refresh that then resolved), drop the result
      // instead of resurrecting the logged-out session.
      if (!get().isAuthenticated) return;

      // Access token → memory only. The server rotated the HttpOnly refresh cookie in
      // the same response; we never write tokens to localStorage. Clean up any legacy
      // JS-readable token from a pre-#39 session on the way through.
      clearLegacyTokenStorage();
      set((state) => ({
        user: state.user ? { ...state.user, ...tokens } : null,
      }));
      broadcastTokens(tokens.token);
    },

    refreshToken: async (): Promise<boolean> => {
      if (typeof window === "undefined") return false;

      // Nothing to refresh once the session is gone — e.g. a request that 401'd during
      // logout would otherwise drive a redundant teardown / ghost refresh.
      if (!get().isAuthenticated) return false;

      // A1: coalesce all concurrent callers behind one in-flight promise.
      if (refreshing) return refreshing;

      refreshing = (async () => {
        try {
          return await performRefresh();
        } finally {
          refreshing = null;
        }
      })();

      return refreshing;
    },

    startTokenRefreshMonitoring: () => {
      if (typeof window === "undefined") return;

      console.log("[Auth Store] Starting token refresh monitoring");

      // Register cross-tab + wake listeners (idempotent).
      setupClientListeners();

      // Clear existing interval
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }

      // Check token every minute (a coarse backstop; the wake listeners cover the gaps
      // the interval misses when a tab is backgrounded/asleep).
      refreshInterval = setInterval(async () => {
        const state = get();
        if (!state.isAuthenticated || !state.user?.token) {
          return;
        }

        // Check if token needs refresh (5 minutes before expiry)
        if (needsTokenRefresh(state.user.token)) {
          console.log(
            "[Auth Store] Token needs refresh, refreshing proactively..."
          );
          await state.refreshToken();
        }
      }, 60 * 1000); // Check every minute
    },

    stopTokenRefreshMonitoring: () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
    },

    initializeAuth: async () => {
      if (typeof window === "undefined") {
        return; // Skip on server-side
      }

      const legacyAccessToken = localStorage.getItem("auth-token");
      const legacyRefreshToken = localStorage.getItem("refresh-token");
      const userInfoStr = localStorage.getItem("user-info");
      const userInfo = parseUserInfo(userInfoStr);

      // Path 1: a full legacy/injected session in localStorage — a mobile WebView that
      // injects both tokens, or a not-yet-migrated pre-#39 web session. (A lone access
      // token, e.g. an MFA-challenge partial token, is intentionally NOT a session.)
      // Adopt it into memory; if the access token is already expired, rotate first
      // (A6) — a web session thereby migrates onto the cookie and clears localStorage.
      if (legacyAccessToken && legacyRefreshToken) {
        set({
          user: {
            token: legacyAccessToken,
            refreshToken: legacyRefreshToken,
            userInfo,
          },
          isAuthenticated: true,
        });
        get().startTokenRefreshMonitoring();
        broadcastTokens(legacyAccessToken);

        if (isTokenExpired(legacyAccessToken)) {
          await get().refreshToken();
        }

        set({ isInitialized: true });
        return;
      }

      // Path 2: no full localStorage session. If a prior session left a marker
      // (user-info) and an HttpOnly refresh cookie survives the reload, silently
      // re-bootstrap before releasing the init gate. A truly anonymous visitor (no
      // marker) skips the network call entirely.
      if (userInfoStr) {
        const bootstrapped = await bootstrapFromCookie(userInfo);
        if (bootstrapped) {
          set({ isInitialized: true });
          return;
        }
        // Cookie gone/expired — clear the stale marker so we don't retry forever.
        localStorage.removeItem("user-info");
      }

      set({ user: null, isAuthenticated: false, isInitialized: true });
    },
  };
});
